import { of } from 'rxjs';
import { BehkhaanRoutes, htmlUrls } from '@shared/utils/router-paths';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '@shared/module/shared.module';
import { ReplaceTitlePipe } from '../pipes/replace-title.pipe';
import { Component, HostListener, OnInit, makeStateKey, TransferState, inject } from '@angular/core';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { BaseComponent } from '@shared/components/base/base.component';
import { Title } from '@angular/platform-browser';
import { BookGenreModel } from '@app/pages/genres/model/book-genre-model';
import { GenresService } from '@app/pages/genres/services/genre.service';
import { SearchService } from '@shared/service/http_services/search.service';
import { bookAuthorModel, BookModel } from '@shared/model/bookModels/book-model';
import { ITagContent, SEOService } from '@shared/service/other_services/seo.service';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { BehkhaanBookCardComponent } from '@app/pages/books/components/behkhaan-book-card/behkhaan-book-card.component';
import { SkeletonBehkhaanBookCardComponent } from '@shared/components/skeletons/skeleton-behkhaan-book-card/skeleton-behkhaan-book-card.component';
import { EImageType } from '@shared/pipes/image.pipe';
import { environment } from '@environments/environment';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';

const STATE_KEY_GENRE = makeStateKey<TServiceResult<BookGenreModel>>('loaded_genre');

@Component({
    selector: 'app-genre-books',
    templateUrl: './genre-books.component.html',
    styleUrls: ['./genre-books.component.scss'],
    imports: [
        SharedModule,
        ReplaceTitlePipe,
        BehkhaanBookCardComponent,
        SkeletonBehkhaanBookCardComponent,
        InfiniteScrollDirective
    ]
})
export class GenreBooksComponent extends BaseComponent implements OnInit {
  private title = inject(Title);
  private router = inject(Router);
  private seoService = inject(SEOService);
  private serverState = inject(TransferState);
  private genresService = inject(GenresService);
  private searchService = inject(SearchService);
  private activatedRoute = inject(ActivatedRoute);
  private _localRepository = inject(LocalRepository);

  books: BookModel[] = []
  totalCount!: number;

  routes = htmlUrls;
  authors: bookAuthorModel[] = [];
  genreInfo: BookGenreModel = new BookGenreModel();

  loading = true;
  scrolled!: boolean;
  isPopularGenre!: boolean;
  loadingBooks!: boolean;
  hasMoreBooks!: boolean;
  bookArr!: any;

  genreBooksTermFilter: TermFilterModel = new TermFilterModel();
  genreId!: string;

  section!: HTMLElement;
  replaceTitle: ReplaceTitlePipe = new ReplaceTitlePipe();
  imageType = EImageType;

  constructor() {
    super();
    this.genreBooksTermFilter.pgNumber = 0;
    this.genreBooksTermFilter.pgSize = 20;
  }

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        switchMap(params => {
          if (params['id']) {
            this.genreId = params['id'];
            const arr = [];
            arr.push(params['id']);
            this.bookArr = arr;
          }
          return of(this.serverState.get<TServiceResult<BookGenreModel>>(STATE_KEY_GENRE, new TServiceResult<BookGenreModel>(new BookGenreModel(), '', '', true, '')))
        }),
        switchMap(cachResult => {
          if (!cachResult.hasError && cachResult.result) {
            if (cachResult.result.id == this.genreId)
              return of(cachResult)
          }
          return this.genresService.getGenreById(this.genreId)
        }),
        switchMap(genreInfo => {
          if (!genreInfo.hasError && genreInfo.result) {
            this.genreInfo = genreInfo.result;

            if (this.genreInfo?.setting?.backgroundColor) {
              this.isPopularGenre = true;
            }
            else {
              const genreImage = this.utilFunctions.getImages(9, this.genreInfo.id);
            }

            // ! Never Remove it.  -- N E V E R --
            this.updateMetaTags();
          }

          return this.searchService.suggestByGenreIds(this.bookArr, this.genreBooksTermFilter)
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(res => {
        if (!res.hasError && res.result) {
          this.books = res.result?.items;
          this.totalCount = res.result.totalCount;
          this.hasMoreBooks = res.result?.hasNextPage;
        }

        this.loading = false
      });
  }

  getRowHeight() {
    const width = window.innerWidth;

    if (width >= 768) return 260.95;
    if (width >= 576) return 215.41;
    return 98;
  }

  back() {
    this.router.navigate([this.routes.home])
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this._localRepository.IsInBrowser && this._localRepository.IsInMobile) {
      const scrollY = this.searchService.Repository.getWindow?.scrollY!;
      const header = this._localRepository.getDocument.getElementById('header') as HTMLElement;
      if (scrollY > 120) {
        this.scrolled = true;
        header.classList.add('scrolled-header');
      }
      if (scrollY < 35) {
        this.scrolled = false;
        header.classList.remove('scrolled-header');
      }
    }
  }

  loadMoreBooks() {
    if (!this.hasMoreBooks) return;

    this.genreBooksTermFilter.pgNumber++;
    this.loadingBooks = true;

    this.searchService.suggestByGenreIds(this.bookArr, this.genreBooksTermFilter)
      .pipe(finalize(() => this.loadingBooks = false))
      .subscribe(res => {
        if (res.hasError || !res.result) return;

        res.result?.items.forEach(book => this.books.push(book));
        this.hasMoreBooks = res.result?.hasNextPage;
      });
  }

  /**
 * ! Warning This Function update all SEO Meta tags
 *
 * ! Never Change it.  -- N E V E R --
 *
 */
  updateMetaTags() {
    const title = this.replaceTitle.transform(this.genreInfo.title!);
    const seoContent: ITagContent = {
      robots: 'index',
      canonical: `${environment.SiteUrl}${BehkhaanRoutes.Genre.base}/${this.genreInfo.id}`,
      title,
      url: environment.SiteUrl + this.router.url,
      description: `${this.genreInfo.info}`,
      image: `${this.environment.ImageUrl}/genres/${this.genreInfo.id}/.jpg`,
    }
    this.seoService.updateTags(seoContent);
    this.title.setTitle(`بهترین کتاب‌های ژانر ${title} | بهخوان`);
  }
}
